// src/SignUpBeta.jsx
import { useState } from "react";
import { auth, db } from "./firebase";
import { createUserWithEmailAndPassword, sendEmailVerification } from "firebase/auth";
import { doc, setDoc, getDoc, serverTimestamp } from "firebase/firestore";
import { useNavigate } from "react-router-dom";

const TRELLI_GREEN = "#00B074";

const goals = [
  "Get back on budget",
  "Understand where my money goes",
  "Pay down debt",
  "Build savings",
  "Other",
];

const features = [
  "Proactive budget alerts",
  "Less-manual budgeting",
  "Budget reporting & insights",
  "Category buckets / virtual cards",
  "Other",
];

export default function SignUpBeta() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [goal, setGoal] = useState(goals[0]);
  const [goalOther, setGoalOther] = useState("");
  const [feat, setFeat] = useState(features[0]);
  const [featOther, setFeatOther] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);

  async function handleCreate(e) {
    e.preventDefault();
    setErr("");
    setBusy(true);

    try {
      // 1) Create Firebase Auth user
      const cred = await createUserWithEmailAndPassword(auth, email, pw);
      const user = cred.user;

      // 2) Create/merge user doc with plan info + survey
      const userRef = doc(db, "users", user.uid);
      const existing = await getDoc(userRef);

      const plan = {
        planTier: "beta",            // 🔒 used for gating later
        planLabel: "Beta (free)",
        planBilling: "free",
        planSince: serverTimestamp(),
        planStatus: "active",
      };

      await setDoc(
        userRef,
        {
          email: user.email,
          createdAt: existing.exists() ? existing.data().createdAt || serverTimestamp() : serverTimestamp(),
          updatedAt: serverTimestamp(),
          plaidConnected: false,
          role: "primary",
          ...plan,
          signupSurvey: {
            goal: goal === "Other" ? `Other: ${goalOther || ""}` : goal,
            feature: feat === "Other" ? `Other: ${featOther || ""}` : feat,
            source: "signup_beta_v1",
          },
          onboarding: {
            setupStep: "mfa",          // mfa -> plaid -> notifications -> done
            featuresTour: "pending",  // pending -> active -> completed | snoozed | dismissed
            featureStep: "dashboard"  // dashboard -> budget -> transactions -> reports -> rules -> goals
          },
        },
        { merge: true }
      );

      // 3) Kick off email verification (useful before MFA)
      try { await sendEmailVerification(user); } catch {}

      setOk(true);
    } catch (e) {
      setErr(e?.message || String(e));
    } finally {
      setBusy(false);
    }
  }

  if (ok) {
    return (
      <div style={wrap}>
        <div style={card}>
          <h2 style={{ marginTop: 0 }}>Account created 🎉</h2>
          <p style={{ color: "#475467" }}>
            We sent a verification email to <b>{email}</b>. Next, you’ll secure your account with 2FA.
          </p>
          <p style={{ color: "#475467", marginTop: 8 }}>
            Click continue to finish setup.
          </p>
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button
                onClick={() => nav("/signup/terms")}
                style={primary}
            >
            Continue
            </button>
            <button
              onClick={() => nav("/")}
              style={linkBtn}
            >
              Go to sign in
            </button>
          </div>
          <div style={{ marginTop: 12, fontSize: 12, color: "#667085" }}>
            (If you’re already signed in, Trelli will guide you to finish securing your account.)
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={wrap}>
      <div style={card}>
        <h2 style={{ marginTop: 0 }}>Beta sign-up</h2>
        <p style={{ color: "#475467", marginTop: 4 }}>
          Create your account, then we’ll finish by securing it with 2FA.
        </p>

        {err && <div style={errorBox}>{err}</div>}

        <form onSubmit={handleCreate} style={{ display: "grid", gap: 10 }}>
          <label style={label}>Email</label>
          <input
            type="email"
            required
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="you@email.com"
            style={input}
            autoComplete="email"
          />

          <label style={label}>Password</label>
          <input
            type="password"
            required
            value={pw}
            onChange={e => setPw(e.target.value)}
            placeholder="At least 6 characters"
            style={input}
            autoComplete="new-password"
          />

          <label style={{ ...label, marginTop: 8 }}>What’s your main goal with Trelli?</label>
          <select value={goal} onChange={e => setGoal(e.target.value)} style={input}>
            {goals.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
          {goal === "Other" && (
            <input
              style={input}
              placeholder="Tell us your goal"
              value={goalOther}
              onChange={e => setGoalOther(e.target.value)}
            />
          )}

          <label style={{ ...label, marginTop: 8 }}>Which feature are you most excited about?</label>
          <select value={feat} onChange={e => setFeat(e.target.value)} style={input}>
            {features.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
          {feat === "Other" && (
            <input
              style={input}
              placeholder="Tell us the feature"
              value={featOther}
              onChange={e => setFeatOther(e.target.value)}
            />
          )}

          <button type="submit" disabled={busy} style={primary}>
            {busy ? "Creating…" : "Create account"}
          </button>
        </form>

        <button onClick={() => nav("/signup")} style={linkUnder}>
          Back to plan selection
        </button>
      </div>
    </div>
  );
}

/* styles */
const wrap = {
  minHeight: "100dvh",
  display: "grid",
  placeItems: "center",
  background:
    "radial-gradient(1200px 600px at 10% -20%, rgba(0,176,116,0.08), transparent 50%), radial-gradient(800px 400px at 110% 120%, rgba(244,197,66,0.06), transparent 40%), #f8faf9",
  padding: 24
};
const card = {
  width: "100%",
  maxWidth: 520,
  background: "#fff",
  borderRadius: 16,
  boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
  padding: 24
};
const label = { fontSize: 13, color: "#444", marginBottom: 6, fontWeight: 600 };
const input = {
  height: 42, borderRadius: 10, border: "1px solid #dce1e5",
  padding: "0 12px", outline: "none", fontSize: 15, background: "#fff"
};
const primary = {
  marginTop: 12, height: 44, border: "none", borderRadius: 12,
  background: TRELLI_GREEN, color: "#fff", fontWeight: 800, fontSize: 15, cursor: "pointer",
  boxShadow: "0 4px 10px rgba(0,176,116,0.25)", width: 180
};
const linkBtn = {
  height: 44, borderRadius: 12, border: "1px solid #E5E7EB",
  background: "#fff", color: "#111", fontWeight: 700, cursor: "pointer", padding: "0 16px"
};
const linkUnder = {
  marginTop: 10, border: "none", background: "transparent",
  color: TRELLI_GREEN, fontWeight: 700, textDecoration: "underline", cursor: "pointer"
};
const errorBox = {
  border: "1px solid #ef9a9a",
  background: "#ffebee",
  color: "#b71c1c",
  borderRadius: 10,
  padding: "10px 12px",
  marginBottom: 12,
  fontSize: 14,
};